import React, { useState } from 'react';
import * as SharedStyled from '@common/styles';
import { Section } from '@common/components/Section/Section';
import { SectionsWrap, DateWrapper, ContentWrapper } from './styles';
import { Select } from '@common/components';
import { ISelectOption } from '@common/types';
import { HookFormDatePicker } from '@common/components/DatePickerWrapper/HookFormDatePicker';
import { AuditsForm, useAuditsForm } from './useAuditsForm';
import { FormProvider } from 'react-hook-form';
import { ContentReportFilters } from './ContentReportFilters';
import { ProductReportFilters } from './ProductReportFilters';
import SubmissionFlowFooter from '@common/components/SubmissionFlowFooter/SubmissionFlowFooter';
import { NotifyModal } from '@common/components/NotifyModal';
import { useGetProductReportData } from './hooks/useGetProductReportData';
import { useGetContentReportData } from './hooks/useGetContentReportData';
import { useGetBroadcastAuditReportData } from './hooks/useGetBroadcastAuditReportData/useGetBroadcastAuditReportData';
import { useFeatureFlags } from '@common/hooks/useFeatureFlags';
import { useDownloadAndOpenFile } from '@common/hooks/useDownloadAndOpenFile';
import { getIsoStringWithTimezoneHandling } from '@common/helpers';
import { useDownloadReportModal } from './hooks/useDownloadReportModal';
import { DownloadReportFileModalContent } from '../../components/DownloadReportFileModalContent';
import { ModalTypes } from '@common/components/NotifyModal/NotifyModal';
import { DocumentType } from '@common/types';

export enum AuditReportType {
  ContentAuditReport = 'ContentAuditReport',
  ProductAuditReport = 'ProductAuditReport',
  ChangeBroadcastReport = 'ChangeBroadcastReport',
}

const { AudioVideo, Audio, Video } = DocumentType;

const Audits = () => {
  const { enableChangeBroadcastReport } = useFeatureFlags();
  const [isOpen, setIsOpen] = useState(false);

  const reportOptions = [
    { label: 'Content report', value: AuditReportType.ContentAuditReport },
    { label: 'Product report', value: AuditReportType.ProductAuditReport },
  ];

  if (enableChangeBroadcastReport) {
    reportOptions.push({ label: 'Change Broadcast report', value: AuditReportType.ChangeBroadcastReport });
  }

  const [selectedReport, setSelectedReport] = useState<ISelectOption>(reportOptions[0]);
  const { methods, reset, handleSubmit } = useAuditsForm(selectedReport.value as AuditReportType);

  const { getAnchorParams } = useDownloadAndOpenFile();
  const { downloadReportModalState, showDownloadReportModal, hideDownloadReportModal } = useDownloadReportModal();

  const reportErrorCallback = () => {
    setIsOpen(true);
  };

  const generateReportCallback = (response: any) => {
    const { url, cleanFileName } = getAnchorParams(response);

    showDownloadReportModal(url, cleanFileName);
  };

  const { generateProductReport } = useGetProductReportData({
    successCallback: generateReportCallback,
    errorCallback: reportErrorCallback,
  });

  const { generateContentReport } = useGetContentReportData({
    successCallback: generateReportCallback,
    errorCallback: reportErrorCallback,
  });

  const { generateBroadcastAuditReport } = useGetBroadcastAuditReportData({
    successCallback: generateReportCallback,
    errorCallback: reportErrorCallback,
  });

  const handleReportChange = (value: ISelectOption) => {
    setSelectedReport(value);
  };

  const handleCancel = () => {
    reset();
  };

  const handleConfirm = () => {
    setIsOpen(false);
  };

  const handleContinue = () => {
    handleSubmit((data) => {
      const defaultFilterSet = {
        from: data.fromDate ? getIsoStringWithTimezoneHandling(data.fromDate) : null,
        to: data.toDate ? getIsoStringWithTimezoneHandling(data.toDate) : null,
      };

      switch (selectedReport.value) {
        case AuditReportType.ContentAuditReport: {
          addPubv3AudioVideoIfLegacyAudioVideoRequested(data.contentTypes);

          const filters = {
            ...defaultFilterSet,
            documentTypes: data.contentTypes ?? [],
            includeAllVersionsContent: data.includeAllVersionsContent,
            includeRetired: data.includeRetiredContent,
            columns: data.contentColumns ?? [],
          };
          generateContentReport(filters);
          break;
        }
        case AuditReportType.ProductAuditReport: {
          const filters = {
            ...defaultFilterSet,
            statuses: data.productStatuses ?? [],
            columns: data.productColumns ?? [],
          };
          generateProductReport(filters);
          break;
        }
        case AuditReportType.ChangeBroadcastReport: {
          // Only date range is needed for broadcast audit
          generateBroadcastAuditReport(defaultFilterSet);
          break;
        }
      }
    })();
  };

  return (
    <>
      <FormProvider {...methods}>
        <SharedStyled.SubmissionFlowWrap>
          <SharedStyled.SubmissionFlowInner>
            <SharedStyled.PageTitle>Download a Report</SharedStyled.PageTitle>
            <SharedStyled.PageSubTitle>
              Use this step by step process to manage your portfolio of products
            </SharedStyled.PageSubTitle>
            <SectionsWrap>
              <Section text="What report would you like to generate?">
                <Select name={''} value={selectedReport} options={reportOptions} onChange={handleReportChange} className="auditType" />
              </Section>
              <Section text="Enter date range">
                <DateWrapper>
                  <HookFormDatePicker name={AuditsForm.fromDate} label="Date from" maxDate={new Date()} />
                  <HookFormDatePicker name={AuditsForm.toDate} label="Date to" maxDate={new Date()} />
                </DateWrapper>
              </Section>
              {selectedReport?.value !== AuditReportType.ChangeBroadcastReport && (
                <Section text="Content report filters">
                  <ContentWrapper>{renderFilters(selectedReport?.value)}</ContentWrapper>
                </Section>
              )}
            </SectionsWrap>
          </SharedStyled.SubmissionFlowInner>
        </SharedStyled.SubmissionFlowWrap>
        <SubmissionFlowFooter onCancel={handleCancel} onContinue={handleContinue} />
      </FormProvider>
      <NotifyModal isOpen={isOpen} onConfirm={handleConfirm}>
        Report could not be generated
      </NotifyModal>
      <NotifyModal
        modalType={ModalTypes.info}
        title="Your report file is ready"
        isOpen={downloadReportModalState.isOpen}
        onClose={hideDownloadReportModal}
        cancelText="Close"
      >
        <DownloadReportFileModalContent modalParams={downloadReportModalState} />
      </NotifyModal>
    </>
  );
};

const renderFilters = (reportType: string) => {
  switch (reportType) {
    case AuditReportType.ContentAuditReport:
      return <ContentReportFilters />;
    case AuditReportType.ProductAuditReport:
      return <ProductReportFilters />;
    case AuditReportType.ChangeBroadcastReport:
      return null;
    default:
      return null;
  }
};

const addPubv3AudioVideoIfLegacyAudioVideoRequested = (contentTypes: ISelectOption[]) => {
  if (contentTypes.filter((col) => col.value === AudioVideo).length > 0) {
    contentTypes.push(
      ...[
        { value: Audio, label: Audio },
        { value: Video, label: Video },
      ],
    );
  }
};

export default Audits;
